import { get, create, update, deleteResource } from "./apiService";
import { resolveBusinessUrlWithQuery } from "../utils/businessRoute";
import type {
  ApiResponse,
  V2Rate,
  ConvertResult,
  V2Order,
  CreateOrderRequest,
  CreateTradeRequest,
  PaginatedResponse,
  PriceAlert,
  CreatePriceAlertRequest,
  UpdatePriceAlertRequest,
} from "../types/api";

// ─── Request Deduplication ──────────────────────────────────────────────────

const pendingRequests = new Map<string, Promise<unknown>>();
const fiatSymbols = new Set(["ZAR", "USD", "EUR", "GBP"]);

function extractRateSymbol(assetIdOrSymbol: string, pair?: string): string {
  if (!pair) {
    return assetIdOrSymbol.toUpperCase();
  }

  const normalizedPair = pair.trim().toUpperCase();
  const [baseSymbol] = normalizedPair.split(/[-/]/);

  return (baseSymbol || assetIdOrSymbol).trim().toUpperCase();
}

interface TradeQuoteView {
  incomingAmount: number;
  outgoingAmount: number;
  inputAmountSide?: "incoming" | "outgoing";
  serviceFee: number;
  feePercentage: {
    $numberDecimal: string;
  };
  pair: string;
  rate: number;
  tradeType?: string;
  quoteId?: string;
  createdAt?: number;
  expiresAt?: number;
}

interface TradePriceView {
  symbol?: string;
  name?: string;
  priceZar?: number;
  priceUsd?: number;
  change24h?: number;
  changePercent24h?: number;
  volume24h?: number;
  marketCap?: number;
  lastUpdated?: string;
  buyRate?: string;
  sellRate?: string;
  rate: number;
}

function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function getNumericRateFromTradePriceView(
  view: Record<string, unknown> | null | undefined
): number {
  if (!view) {
    return 0;
  }
  const direct = parseFiniteNumber(view.rate);
  if (direct !== null && direct > 0) {
    return direct;
  }
  const buy = parseFiniteNumber(view.buyRate);
  if (buy !== null && buy > 0) {
    return buy;
  }
  const sell = parseFiniteNumber(view.sellRate);
  if (sell !== null && sell > 0) {
    return sell;
  }
  return 0;
}

function resolveTradePrice(
  rate: Record<string, unknown>,
  pair?: string,
  tradeType?: string
): number {
  const normalizedQuote = pair?.split(/[-/]/)[1]?.trim().toUpperCase();
  const normalizedTradeType = tradeType?.trim().toLowerCase();

  if (normalizedQuote === "ZAR") {
    const priceZar = parseFiniteNumber(rate.priceZar);
    if (priceZar !== null) {
      return priceZar;
    }
  }

  if (
    normalizedQuote === "USD" ||
    normalizedQuote === "USDT" ||
    normalizedQuote === "USDC"
  ) {
    const priceUsd = parseFiniteNumber(rate.priceUsd);
    if (priceUsd !== null) {
      return priceUsd;
    }
  }

  const directionalRate =
    normalizedTradeType === "sell"
      ? parseFiniteNumber(rate.sellRate)
      : parseFiniteNumber(rate.buyRate);
  if (directionalRate !== null) {
    return directionalRate;
  }

  const fallbackDirectionalRate =
    parseFiniteNumber(rate.sellRate) ?? parseFiniteNumber(rate.buyRate);
  if (fallbackDirectionalRate !== null) {
    return fallbackDirectionalRate;
  }

  return (
    parseFiniteNumber(rate.priceZar) ?? parseFiniteNumber(rate.priceUsd) ?? 0
  );
}

function normalizeQuotePair(
  pair: string | undefined,
  incomingSymbol: string,
  outgoingSymbol: string
): string {
  if (pair && pair.trim().length > 0) {
    return pair.replace("/", "-").toUpperCase();
  }

  return `${incomingSymbol.toUpperCase()}-${outgoingSymbol.toUpperCase()}`;
}

function deriveDisplayRate(
  pair: string,
  outgoingSymbol: string,
  incomingSymbol: string,
  outgoingAmount: number,
  incomingAmount: number,
  directRate: number
): number {
  const [baseSymbol = "", quoteSymbol = ""] = pair.split("-");
  const upperOutgoing = outgoingSymbol.toUpperCase();
  const upperIncoming = incomingSymbol.toUpperCase();

  if (outgoingAmount <= 0 || incomingAmount <= 0) {
    return 0;
  }

  if (upperOutgoing === quoteSymbol && upperIncoming === baseSymbol) {
    return outgoingAmount / incomingAmount;
  }

  if (upperOutgoing === baseSymbol && upperIncoming === quoteSymbol) {
    return incomingAmount / outgoingAmount;
  }

  if (fiatSymbols.has(upperOutgoing) && !fiatSymbols.has(upperIncoming)) {
    return outgoingAmount / incomingAmount;
  }

  if (!fiatSymbols.has(upperOutgoing) && fiatSymbols.has(upperIncoming)) {
    return incomingAmount / outgoingAmount;
  }

  return directRate;
}

function toTradeQuoteView(
  data: {
    amount?: number | string;
    incomingAmount?: number | string;
    amountSide?: "incoming" | "outgoing";
    asset?: string;
    incomingAsset?: string;
    outgoingAsset?: string;
    from?: string;
    to?: string;
    pair?: string;
  },
  result: ConvertResult
): TradeQuoteView {
  const amountSide = data.amountSide === "incoming" ? "incoming" : "outgoing";
  const outgoingSymbol = (
    data.outgoingAsset ||
    data.from ||
    data.asset ||
    result.from ||
    ""
  ).toUpperCase();
  const incomingSymbol = (
    data.incomingAsset ||
    data.to ||
    result.to ||
    ""
  ).toUpperCase();
  const normalizedPair = normalizeQuotePair(
    data.pair,
    incomingSymbol,
    outgoingSymbol
  );
  const outgoingAmount =
    amountSide === "incoming" ? Number(result.result) : Number(result.amount);
  const incomingAmount =
    amountSide === "incoming" ? Number(result.amount) : Number(result.result);
  const directRate = Number(result.rate);

  return {
    incomingAmount,
    outgoingAmount,
    inputAmountSide: amountSide,
    serviceFee: 0,
    feePercentage: { $numberDecimal: "0" },
    pair: normalizedPair,
    rate: deriveDisplayRate(
      normalizedPair,
      outgoingSymbol,
      incomingSymbol,
      outgoingAmount,
      incomingAmount,
      directRate
    ),
  };
}

function wrapData<T>(data: T): { data: ApiResponse<T> } {
  return {
    data: {
      status: true,
      data,
    },
  };
}

async function dedup<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fn().finally(() => {
    if (pendingRequests.get(key) === promise) {
      pendingRequests.delete(key);
    }
  });

  pendingRequests.set(key, promise);
  return promise;
}

// ─── Rates ──────────────────────────────────────────────────────────────────

/**
 * Get all exchange rates.
 */
export const getAllRates = async (): Promise<V2Rate[]> => {
  return get<V2Rate[]>("/api/v2/rates");
};

/**
 * Get the exchange rate for a specific asset symbol.
 * Replaces the old getTradeRate(assetId, tradeType, pair, isPostTrade).
 */
export const getRate = async (symbol: string): Promise<V2Rate | null> => {
  const requestKey = `rate_${symbol}`;

  return dedup(requestKey, async () => {
    return get<V2Rate | null>(`/api/v2/rates/${encodeURIComponent(symbol)}`);
  });
};

/**
 * Convert an amount from one asset to another.
 * Supersedes the old quote endpoints by routing both buy/sell flows
 * through the shared convert quote endpoint.
 */
export const getConvertQuote = async (
  from: string,
  to: string,
  amount: number | string
): Promise<ConvertResult> => {
  const parsedAmount =
    typeof amount === "string" ? parseFloat(amount.replace(/,/g, "")) : amount;

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    throw new Error("Please enter a valid amount greater than zero");
  }

  // V2 ENDPOINT: GET /api/v2/rates/convert?from=X&to=Y&amount=Z
  const result = await get<ConvertResult>("/api/v2/rates/convert", {
    from,
    to,
    amount: String(parsedAmount),
  });

  if (!result) {
    throw new Error("Could not generate quote");
  }

  return result;
};

/**
 * UI quote wrapper.
 * Accepts Buy/Sell form data and maps it to the v2 convert endpoint.
 */
export const getQuote = async (data: {
  amount?: number | string;
  incomingAmount?: number | string;
  amountSide?: "incoming" | "outgoing";
  asset?: string;
  from?: string;
  to?: string;
  incomingAsset?: string;
  outgoingAsset?: string;
  pair?: string;
  [key: string]: unknown;
}): Promise<{ data: ApiResponse<TradeQuoteView> }> => {
  const amountSide = data.amountSide === "incoming" ? "incoming" : "outgoing";
  const requestAmount =
    amountSide === "incoming" ? data.incomingAmount : data.amount;

  if (requestAmount === null || requestAmount === undefined) {
    throw new Error("Amount is required to generate a quote");
  }

  const from =
    amountSide === "incoming"
      ? ((data.incomingAsset || data.to || "") as string)
      : ((data.from || data.outgoingAsset || data.asset || "") as string);
  const to =
    amountSide === "incoming"
      ? ((data.outgoingAsset || data.from || data.asset || "") as string)
      : ((data.to || data.incomingAsset || "") as string);

  const result = await getConvertQuote(from, to, requestAmount);
  const tradeQuote = toTradeQuoteView({ ...data, amountSide }, result);

  return {
    data: {
      status: true,
      data: tradeQuote,
    },
  };
};

/**
 * Sell quote wrapper.
 */
export const getSellQuote = async (data: {
  amount: number | string;
  from?: string;
  to?: string;
  incomingAsset?: string;
  outgoingAsset?: string;
  [key: string]: unknown;
}): Promise<{ data: ApiResponse<TradeQuoteView> }> => {
  return getQuote(data);
};

/**
 * Get the trade price view used by the trading UI.
 * Maps the v2 market snapshot endpoint into a stable rate-centric payload.
 */
export const getTradeRate = async (
  assetIdOrSymbol: string,
  tradeType?: string,
  pair?: string,
  _isPostTrade?: boolean
): Promise<{ data: ApiResponse<TradePriceView> }> => {
  const symbol = extractRateSymbol(assetIdOrSymbol, pair);

  const rate = await getRate(symbol);
  if (!rate) {
    throw new Error(`Rate not found for ${symbol}`);
  }
  const rateRecord = rate as unknown as Record<string, unknown>;
  const resolvedRate = resolveTradePrice(rateRecord, pair, tradeType);

  return {
    data: {
      status: true,
      data: { ...(rate as TradePriceView), rate: resolvedRate },
    },
  };
};

// ─── Trades ─────────────────────────────────────────────────────────────────

export const createTrade = async (
  data: CreateTradeRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse }> => {
  const businessId =
    typeof data.businessId === "string" && data.businessId.trim()
      ? data.businessId.trim()
      : null;
  const { businessId: _ignoredBusinessId, ...body } =
    data as CreateTradeRequest & { businessId?: string | null };
  const response = await create<Record<string, unknown>>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/trade"),
    body
  );
  return wrapData(response);
};

// ─── Orders ─────────────────────────────────────────────────────────────────

/**
 * Get the user's trading orders.
 * v2 derives user from JWT -- no userId needed.
 */
export const getUserOrders = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  pairId?: string;
}): Promise<{ data: PaginatedResponse<V2Order> }> => {
  const response = await get<PaginatedResponse<V2Order>>(
    "/api/v2/trading/orders",
    params as Record<string, string>
  );
  return { data: response };
};

/**
 * Get a single order by ID.
 */
export const getOrderById = async (
  orderId: string
): Promise<{ data: ApiResponse<V2Order> }> => {
  const response = await get<V2Order>(`/api/v2/trading/orders/${orderId}`);
  return wrapData(response);
};

/**
 * Create a trading order (limit or market).
 * Creates a trading order through the v2 trading API.
 */
export const createOrder = async (
  data: CreateOrderRequest
): Promise<{ data: ApiResponse<V2Order> }> => {
  const response = await create<V2Order>("/api/v2/trading/orders", data);
  return wrapData(response);
};

/**
 * Cancel a trading order.
 * v2 uses POST /api/v2/trading/orders/:id/cancel.
 */
export const cancelOrder = async (
  orderId: string
): Promise<{ data: ApiResponse }> => {
  const response = await create<Record<string, unknown>>(
    `/api/v2/trading/orders/${orderId}/cancel`,
    {}
  );
  return wrapData(response);
};

/**
 * Legacy-compatible deleteUserOrders (maps to cancelOrder).
 */
export const deleteUserOrders = async (
  orderId: string
): Promise<{ data: ApiResponse }> => {
  return cancelOrder(orderId);
};

// ─── Price Alerts ───────────────────────────────────────────────────────────

/**
 * Create a price alert.
 */
export const createPriceAlert = async (
  data: CreatePriceAlertRequest
): Promise<{ data: ApiResponse<PriceAlert> }> => {
  const response = await create<PriceAlert>(
    "/api/v2/trading/price-alerts",
    data
  );
  return wrapData(response);
};

/**
 * List user's price alerts.
 */
export const listPriceAlerts = async (): Promise<{
  data: ApiResponse<PriceAlert[]>;
}> => {
  const response = await get<PriceAlert[]>("/api/v2/trading/price-alerts");
  return wrapData(response);
};

/**
 * Update a price alert.
 */
export const updatePriceAlert = async (
  alertId: string,
  data: UpdatePriceAlertRequest
): Promise<{ data: ApiResponse<PriceAlert> }> => {
  const response = await update<PriceAlert, UpdatePriceAlertRequest>(
    `/api/v2/trading/price-alerts/${alertId}`,
    {},
    data
  );
  return wrapData(response);
};

/**
 * Delete a price alert.
 */
export const deletePriceAlert = async (
  alertId: string
): Promise<{ data: ApiResponse }> => {
  const response = await deleteResource<Record<string, unknown>>(
    `/api/v2/trading/price-alerts/${alertId}`
  );
  return wrapData(response);
};
